import React, { useMemo } from "react"
import Table from "../../../components/structure/table/table"
import { rate, formatRate, splitNewRepeat } from "../_utils/data"

// Per-agent conversion leaderboard for the selected period. Enquiries are the
// canonical de-duped set; bookings are every booking attributed to the agent in
// the window. New vs repeat is split off the client-history map.
export default function AgentLeaderboard({ agents, dataset, historyMap }) {
	const rows = useMemo(() => {
		if (!dataset) return []

		const { finalEnquiries, allBookings } = dataset

		return agents
			.map((agent) => {
				const agentEnquiries = finalEnquiries.filter((enquiry) => enquiry.agent === agent.id)
				const agentBookings = allBookings.filter((booking) => booking.agent === agent.id)

				const enquirySplit = splitNewRepeat(agentEnquiries, historyMap)
				const bookingSplit = splitNewRepeat(agentBookings, historyMap)

				return {
					id: agent.id,
					name: agent.name,
					enquiries: agentEnquiries.length,
					bookings: agentBookings.length,
					conversion: rate(agentBookings.length, agentEnquiries.length),
					newConversion: rate(bookingSplit.newCount, enquirySplit.newCount),
					repeatConversion: rate(bookingSplit.repeatCount, enquirySplit.repeatCount),
				}
			})
			.sort((a, b) => b.bookings - a.bookings || (b.conversion || 0) - (a.conversion || 0))
	}, [agents, dataset, historyMap])

	const totals = useMemo(() => {
		const sum = rows.reduce(
			(acc, row) => {
				acc.enquiries += row.enquiries
				acc.bookings += row.bookings
				return acc
			},
			{ enquiries: 0, bookings: 0 }
		)
		return { ...sum, conversion: rate(sum.bookings, sum.enquiries) }
	}, [rows])

	return (
		<Table
			className="stat-highlights-table"
			headings={["Rank", "Sales agent", "Enquiries (de-duped)", "Bookings", "Conversion", "New conv.", "Repeat conv."]}
			noResults={!rows.length}
			noResultsMessage="No agent data for this period">
			{rows.map((row, index) => (
				<tr key={row.id}>
					<td className="rank-cell">{index + 1}</td>
					<td>{row.name}</td>
					<td>{row.enquiries}</td>
					<td>{row.bookings}</td>
					<td className="emphasis-cell">{formatRate(row.conversion)}</td>
					<td>{formatRate(row.newConversion)}</td>
					<td>{formatRate(row.repeatConversion)}</td>
				</tr>
			))}
			{rows.length > 0 && (
				<tr className="totals-row">
					<td />
					<td>
						<strong>Team total</strong>
					</td>
					<td>
						<strong>{totals.enquiries}</strong>
					</td>
					<td>
						<strong>{totals.bookings}</strong>
					</td>
					<td className="emphasis-cell">
						<strong>{formatRate(totals.conversion)}</strong>
					</td>
					<td />
					<td />
				</tr>
			)}
		</Table>
	)
}
